/**
 * Vincent B687 (Eric 2026-02-08) — Cache Inspector TanStack Query.
 *
 * Page admin qui liste EN DIRECT toutes les queries en cache : leur clé,
 * leur statut (fresh/stale/inactive), le nombre d'observers (composants qui
 * consomment la donnée), et l'âge de la dernière mise à jour.
 *
 * Objectif : offrir à Eric/Vincent un outil de debug/audit sans avoir à
 * ouvrir les DevTools TanStack (opt-in via ?devtools=1). Permet aussi de
 * purger le cache d'une clé donnée en 1 clic (utile après un déploiement
 * qui change la forme des données).
 */
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useLang } from "../../contexts/LanguageContext";

function formatKey(queryKey) {
  try {
    return JSON.stringify(queryKey);
  } catch {
    return String(queryKey);
  }
}

function formatAge(ts) {
  if (!ts) return "—";
  const seconds = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  return `${Math.floor(seconds / 3600)}h`;
}

// Vincent B688 (Eric 2026-02-08) — Récupère le "top-level key" (premier
// segment de queryKey si array, ou chaîne entière). Ex : ['jargon','list',...]
// → 'jargon'. Utilisé pour grouper les rows dans la vue Grouped.
function topLevelKey(queryKey) {
  if (Array.isArray(queryKey) && queryKey.length > 0) {
    return String(queryKey[0] ?? "misc");
  }
  return String(queryKey ?? "misc");
}

function statusLabel(query, lang) {
  const state = query.state;
  if (state.status === "pending") return { txt: lang === "fr" ? "chargement" : "loading", cls: "bg-blue-100 text-blue-800" };
  if (state.status === "error") return { txt: lang === "fr" ? "erreur" : "error", cls: "bg-red-100 text-red-800" };
  if (query.isStale()) return { txt: lang === "fr" ? "obsolète" : "stale", cls: "bg-amber-100 text-amber-800" };
  return { txt: lang === "fr" ? "frais" : "fresh", cls: "bg-emerald-100 text-emerald-800" };
}

export default function CacheInspectorPage() {
  const { lang } = useLang();
  const queryClient = useQueryClient();
  const [tick, setTick] = useState(0);
  const [filter, setFilter] = useState("");
  // Vincent B688 (Eric 2026-02-08) — Toggle Grouped/Flat persisté en localStorage.
  const [viewMode, setViewMode] = useState(() => {
    try {
      return localStorage.getItem("cache-inspector-view") || "grouped";
    } catch {
      return "grouped";
    }
  });
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem("cache-inspector-collapsed") || "[]"));
    } catch {
      return new Set();
    }
  });

  useEffect(() => {
    try { localStorage.setItem("cache-inspector-view", viewMode); } catch { /* ignore */ }
  }, [viewMode]);
  useEffect(() => {
    try {
      localStorage.setItem("cache-inspector-collapsed", JSON.stringify([...collapsedGroups]));
    } catch { /* ignore */ }
  }, [collapsedGroups]);

  // Auto-refresh 1s (le cache change en direct au fil des interactions).
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const cache = queryClient.getQueryCache();
  const all = cache.getAll();

  const rows = all
    .map((q) => {
      const st = q.state;
      const data = st.data;
      let entriesCount = "—";
      if (Array.isArray(data)) entriesCount = data.length;
      else if (data && typeof data === "object") {
        if (Array.isArray(data.items)) entriesCount = data.items.length;
        else if (Array.isArray(data.words)) entriesCount = data.words.length;
        else entriesCount = Object.keys(data).length;
      }
      return {
        hash: q.queryHash,
        key: formatKey(q.queryKey),
        group: topLevelKey(q.queryKey),
        status: statusLabel(q, lang),
        entries: entriesCount,
        observers: q.getObserversCount(),
        updatedAt: st.dataUpdatedAt,
        stale: q.isStale(),
        raw: q,
      };
    })
    .filter((r) => !filter || r.key.toLowerCase().includes(filter.toLowerCase()))
    .sort((a, b) => b.updatedAt - a.updatedAt);

  // Vincent B688 — Groupement par top-level key + tri des groupes par nombre
  // décroissant d'entrées (les groupes lourds apparaissent en premier).
  const groups = rows.reduce((acc, r) => {
    (acc[r.group] = acc[r.group] || []).push(r);
    return acc;
  }, {});
  const sortedGroupNames = Object.keys(groups).sort((a, b) =>
    (groups[b].length - groups[a].length) || a.localeCompare(b)
  );

  const toggleGroup = (name) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const invalidateOne = (query) => {
    queryClient.invalidateQueries({ queryKey: query.queryKey, exact: true });
    toast.success(lang === "fr" ? "Clé invalidée (refetch)" : "Key invalidated (refetch)");
  };
  const removeOne = (query) => {
    cache.remove(query);
    toast.success(lang === "fr" ? "Entrée supprimée du cache" : "Entry removed from cache");
    setTick((t) => t + 1);
  };
  const clearAll = () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(
      lang === "fr"
        ? "Purger TOUT le cache TanStack Query ? Les composants montés vont refetch."
        : "Clear ALL TanStack Query cache? Mounted components will refetch."
    )) return;
    queryClient.clear();
    toast.success(lang === "fr" ? "Cache purgé" : "Cache cleared");
    setTick((t) => t + 1);
  };

  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="cache-inspector-page">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-zinc-900" data-testid="cache-inspector-title">
          {lang === "fr" ? "Inspecteur du cache TanStack" : "TanStack Cache Inspector"}
        </h1>
        <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
          {lang === "fr"
            ? "État en direct du cache client (rafraîchi 1×/s). Utilisez « Invalider » pour forcer un refetch en gardant les données existantes, ou « Supprimer » pour les évacuer complètement."
            : "Live client cache state (refreshed 1×/s). Use \"Invalidate\" to refetch while keeping current data, or \"Remove\" to fully evict."}
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
          <span
            className="inline-flex items-center px-2 py-0.5 rounded bg-zinc-100 text-zinc-700 font-medium"
            data-testid="cache-inspector-total"
          >
            {rows.length} {lang === "fr" ? "entrées" : "entries"}
            {filter && (
              <span className="ml-1 text-zinc-400">
                / {all.length} {lang === "fr" ? "au total" : "total"}
              </span>
            )}
          </span>
          <span className="text-zinc-400">tick #{tick}</span>
        </div>
      </header>

      <div className="mb-4 flex flex-wrap gap-3 items-center">
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={lang === "fr" ? "Filtrer par clé…" : "Filter by key…"}
          className="flex-1 min-w-[240px] px-3 py-1.5 text-sm border border-zinc-300 rounded-md focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
          data-testid="cache-inspector-filter"
        />
        {/* Vincent B688 — Toggle Grouped/Flat, persisté en localStorage. */}
        <div
          className="inline-flex items-center rounded-md border border-zinc-300 bg-white text-xs font-medium overflow-hidden"
          role="group"
          aria-label={lang === "fr" ? "Mode d'affichage" : "View mode"}
          data-testid="cache-inspector-view-toggle"
        >
          <button
            type="button"
            onClick={() => setViewMode("grouped")}
            className={`px-3 py-1.5 ${viewMode === "grouped" ? "bg-[#002FA7] text-white" : "text-zinc-700 hover:bg-zinc-50"}`}
            data-testid="cache-inspector-view-grouped"
          >
            {lang === "fr" ? "Groupé" : "Grouped"}
          </button>
          <button
            type="button"
            onClick={() => setViewMode("flat")}
            className={`px-3 py-1.5 border-l border-zinc-300 ${viewMode === "flat" ? "bg-[#002FA7] text-white" : "text-zinc-700 hover:bg-zinc-50"}`}
            data-testid="cache-inspector-view-flat"
          >
            {lang === "fr" ? "Plat" : "Flat"}
          </button>
        </div>
        <button
          type="button"
          onClick={clearAll}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-red-300 text-red-800 bg-white text-xs font-medium hover:bg-red-50"
          data-testid="cache-inspector-clear-all"
        >
          {lang === "fr" ? "Purger tout" : "Clear all"}
        </button>
      </div>

      {rows.length === 0 ? (
        <div className="p-6 text-sm text-zinc-500 border border-dashed border-zinc-300 rounded-md" data-testid="cache-inspector-empty">
          {lang === "fr"
            ? "Aucune entrée en cache. Naviguez dans l'app pour peupler."
            : "No cache entry. Navigate in the app to populate."}
        </div>
      ) : viewMode === "flat" ? (
        <div className="overflow-x-auto border border-zinc-200 rounded-md">
          <table className="w-full text-sm" data-testid="cache-inspector-table">
            <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
              <tr>
                <th className="text-left px-3 py-2">{lang === "fr" ? "Clé" : "Key"}</th>
                <th className="text-left px-3 py-2">{lang === "fr" ? "Statut" : "Status"}</th>
                <th className="text-right px-3 py-2">{lang === "fr" ? "Entrées" : "Entries"}</th>
                <th className="text-right px-3 py-2">Obs.</th>
                <th className="text-right px-3 py-2">{lang === "fr" ? "MàJ" : "Age"}</th>
                <th className="text-right px-3 py-2 pr-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => renderRow(r, invalidateOne, removeOne, lang))}
            </tbody>
          </table>
        </div>
      ) : (
        // Vincent B688 — Vue Grouped : sections repliables par top-level key.
        <div className="space-y-3" data-testid="cache-inspector-groups">
          {sortedGroupNames.map((name) => {
            const items = groups[name];
            const isCollapsed = collapsedGroups.has(name);
            const totalEntries = items.reduce(
              (s, r) => s + (typeof r.entries === "number" ? r.entries : 0),
              0
            );
            return (
              <section
                key={name}
                className="border border-zinc-200 rounded-md overflow-hidden"
                data-testid={`cache-group-${name}`}
              >
                <button
                  type="button"
                  onClick={() => toggleGroup(name)}
                  className="w-full flex items-center justify-between gap-3 px-3 py-2 bg-zinc-50 hover:bg-zinc-100 text-left"
                  data-testid={`cache-group-header-${name}`}
                >
                  <span className="flex items-center gap-2 text-sm font-semibold text-zinc-900">
                    <span className="text-zinc-400 text-xs w-3 inline-block">
                      {isCollapsed ? "▶" : "▼"}
                    </span>
                    <code className="text-[#002FA7]">{name}</code>
                    <span className="text-xs font-normal text-zinc-500">
                      [{items.length} {lang === "fr" ? "entrées" : "entries"}
                      {totalEntries > 0 && ` · ${totalEntries} ${lang === "fr" ? "items totaux" : "total items"}`}]
                    </span>
                  </span>
                </button>
                {!isCollapsed && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid={`cache-group-table-${name}`}>
                      <thead className="bg-white text-[10px] uppercase text-zinc-500 border-t border-zinc-200">
                        <tr>
                          <th className="text-left px-3 py-1.5">{lang === "fr" ? "Clé" : "Key"}</th>
                          <th className="text-left px-3 py-1.5">{lang === "fr" ? "Statut" : "Status"}</th>
                          <th className="text-right px-3 py-1.5">{lang === "fr" ? "Entrées" : "Entries"}</th>
                          <th className="text-right px-3 py-1.5">Obs.</th>
                          <th className="text-right px-3 py-1.5">{lang === "fr" ? "MàJ" : "Age"}</th>
                          <th className="text-right px-3 py-1.5 pr-4">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((r) => renderRow(r, invalidateOne, removeOne, lang))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Row rendering helper (partagé entre Grouped et Flat).
function renderRow(r, invalidateOne, removeOne, lang) {
  return (
    <tr key={r.hash} className="border-t border-zinc-100" data-testid={`cache-row-${r.hash.slice(0, 20)}`}>
      <td className="px-3 py-2 font-mono text-xs text-zinc-800 max-w-md truncate" title={r.key}>
        {r.key}
      </td>
      <td className="px-3 py-2">
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${r.status.cls}`}>
          {r.status.txt}
        </span>
      </td>
      <td className="px-3 py-2 text-right tabular-nums">{r.entries}</td>
      <td className="px-3 py-2 text-right tabular-nums">{r.observers}</td>
      <td className="px-3 py-2 text-right tabular-nums text-zinc-500">{formatAge(r.updatedAt)}</td>
      <td className="px-3 py-2 text-right pr-4">
        <div className="inline-flex gap-1">
          <button
            type="button"
            onClick={() => invalidateOne(r.raw)}
            className="px-2 py-1 rounded border border-zinc-300 text-[11px] text-zinc-700 hover:bg-zinc-50"
            title={lang === "fr" ? "Invalider (refetch)" : "Invalidate (refetch)"}
            data-testid={`cache-invalidate-${r.hash.slice(0, 20)}`}
          >
            {lang === "fr" ? "Refetch" : "Refetch"}
          </button>
          <button
            type="button"
            onClick={() => removeOne(r.raw)}
            className="px-2 py-1 rounded border border-zinc-300 text-[11px] text-red-700 hover:bg-red-50"
            title={lang === "fr" ? "Supprimer" : "Remove"}
            data-testid={`cache-remove-${r.hash.slice(0, 20)}`}
          >
            {lang === "fr" ? "Supprimer" : "Remove"}
          </button>
        </div>
      </td>
    </tr>
  );
}
